import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { FormsModule } from '@angular/forms';
import {
  ApiService,
  EventModel,
  EventTicketType,
  EventsListComponent,
  WishlistItem,
  PaginationResponse,
} from '@hypenation/tickets';
import { ClientPortalApiService } from '../../services/client-portal-api.service';
import { FilesService } from '@hypenation/shared';
import { HttpResponse } from '@angular/common/http';

const ACTIVE_PAGE_SIZE = 100;
const COMING_SOON_PAGE_SIZE = 30;

@Component({
  selector: 'hypenation-events',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzSpinModule,
    NzIconModule,
    NzButtonModule,
    NzEmptyModule,
    NzImageModule,
    NzModalModule,
    NzRadioModule,
    FormsModule,
    EventsListComponent,
  ],
  templateUrl: './events.component.html',
  styleUrl: './events.component.css',
})
export class EventsComponent implements OnInit, OnDestroy {
  activeTab: 'active' | 'coming-soon' = 'active';
  loading = false;

  // Active events
  activeEvents: EventModel[] = [];
  activeTotalItems = 0;
  activePage = 0;
  activeSize = ACTIVE_PAGE_SIZE;
  activeLoadingMore = false;

  // Coming soon
  comingSoonEvents: EventModel[] = [];
  comingSoonTotalItems = 0;
  comingSoonPage = 0;
  comingSoonSize = COMING_SOON_PAGE_SIZE;
  comingSoonLoadingMore = false;

  get activeHasMore(): boolean {
    return this.activeEvents.length < this.activeTotalItems;
  }

  get comingSoonHasMore(): boolean {
    return this.comingSoonEvents.length < this.comingSoonTotalItems;
  }

  // Wishlist tracking: set of product UIDs in wishlist
  wishlistUids = new Set<string>();
  // Map of product UID → wishlist item UID (for removal)
  wishlistMap = new Map<string, string>();
  wishlistLoading = new Set<string>();

  // Featured events hero
  featuredEvents: EventModel[] = [];
  featuredEventsLoading = false;
  currentFeaturedIndex = 0;
  featuredImageUrls = new Map<number, string>();
  private featuredInterval: ReturnType<typeof setInterval> | undefined;

  // Image URL cache
  imageUrls = new Map<string, string>();

  // Ticket type selection modal
  ticketTypeModalVisible = false;
  ticketTypeModalEvent: EventModel | null = null;
  ticketTypes: EventTicketType[] = [];
  ticketTypesLoading = false;
  selectedTicketTypeUid: string | null = null;

  private readonly apiService = inject(ApiService);
  private readonly portalApiService = inject(ClientPortalApiService);
  private readonly filesService = inject(FilesService);
  private readonly notification = inject(NzNotificationService);

  ngOnInit(): void {
    this.loadActiveEvents();
    this.loadFeaturedEvents();
    this.loadWishlist();
  }

  switchTab(tab: 'active' | 'coming-soon'): void {
    this.activeTab = tab;
    if (tab === 'coming-soon' && this.comingSoonEvents.length === 0) {
      this.loadComingSoonEvents();
    }
  }

  ngOnDestroy(): void {
    if (this.featuredInterval) clearInterval(this.featuredInterval);
  }

  loadFeaturedEvents(): void {
    this.featuredEventsLoading = true;
    this.apiService.getFeaturedEvents(0, 10).subscribe({
      next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
        this.featuredEvents = res.body?.content ?? [];
        this.featuredEventsLoading = false;
        this.featuredEvents.forEach((event, i) => {
          if (event.imageFileName) {
            this.filesService
              .getFileDownloadUrl(event.imageFileName)
              .subscribe({
                next: (url) => this.featuredImageUrls.set(i, url),
                error: () =>
                  this.featuredImageUrls.set(i, 'assets/placeholder.jpg'),
              });
          }
        });
        if (this.featuredEvents.length > 1) {
          this.featuredInterval = setInterval(() => {
            this.currentFeaturedIndex =
              (this.currentFeaturedIndex + 1) % this.featuredEvents.length;
          }, 6000);
        }
      },
      error: () => (this.featuredEventsLoading = false),
    });
  }

  getFeaturedImageUrl(): string {
    return (
      this.featuredImageUrls.get(this.currentFeaturedIndex) ||
      'assets/placeholder.jpg'
    );
  }

  goToFeatured(index: number): void {
    this.currentFeaturedIndex = index;
    if (this.featuredInterval) {
      clearInterval(this.featuredInterval);
      this.featuredInterval = setInterval(() => {
        this.currentFeaturedIndex =
          (this.currentFeaturedIndex + 1) % this.featuredEvents.length;
      }, 6000);
    }
  }

  loadActiveEvents(): void {
    this.loading = true;
    this.activePage = 0;
    this.apiService
      .getAllActiveEventsPaginated(0, this.activeSize)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          this.activeEvents = res.body?.content ?? [];
          this.activeTotalItems = res.body?.totalElements ?? 0;
          this.loadImages(this.activeEvents);
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  loadMoreActive(): void {
    if (!this.activeHasMore || this.activeLoadingMore) return;
    const nextPage = this.activePage + 1;
    this.activeLoadingMore = true;
    this.apiService
      .getAllActiveEventsPaginated(nextPage, this.activeSize)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          const incoming = res.body?.content ?? [];
          this.activeEvents = dedupeAppend(this.activeEvents, incoming);
          this.activeTotalItems =
            res.body?.totalElements ?? this.activeTotalItems;
          this.activePage = nextPage;
          this.loadImages(incoming);
          this.activeLoadingMore = false;
        },
        error: () => (this.activeLoadingMore = false),
      });
  }

  loadComingSoonEvents(): void {
    this.loading = true;
    this.comingSoonPage = 0;
    this.portalApiService
      .getComingSoonEvents(0, this.comingSoonSize)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          this.comingSoonEvents = res.body?.content ?? [];
          this.comingSoonTotalItems = res.body?.totalElements ?? 0;
          this.loadImages(this.comingSoonEvents);
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  loadMoreComingSoon(): void {
    if (!this.comingSoonHasMore || this.comingSoonLoadingMore) return;
    const nextPage = this.comingSoonPage + 1;
    this.comingSoonLoadingMore = true;
    this.portalApiService
      .getComingSoonEvents(nextPage, this.comingSoonSize)
      .subscribe({
        next: (res: HttpResponse<PaginationResponse<EventModel[]>>) => {
          const incoming = res.body?.content ?? [];
          this.comingSoonEvents = dedupeAppend(this.comingSoonEvents, incoming);
          this.comingSoonTotalItems =
            res.body?.totalElements ?? this.comingSoonTotalItems;
          this.comingSoonPage = nextPage;
          this.loadImages(incoming);
          this.comingSoonLoadingMore = false;
        },
        error: () => (this.comingSoonLoadingMore = false),
      });
  }

  loadWishlist(): void {
    this.apiService.getMyWishlist().subscribe({
      next: (res: HttpResponse<WishlistItem[]>) => {
        const items = res.body ?? [];
        this.wishlistUids.clear();
        this.wishlistMap.clear();
        items.forEach((item) => {
          this.wishlistUids.add(item.productUid);
          this.wishlistMap.set(item.productUid, item.uid);
        });
      },
    });
  }

  loadImages(events: EventModel[]): void {
    events.forEach((event) => {
      if (event.imageFileName && !this.imageUrls.has(event.uid)) {
        this.filesService.getFileDownloadUrl(event.imageFileName).subscribe({
          next: (url) => this.imageUrls.set(event.uid, url),
          error: () => this.imageUrls.set(event.uid, 'assets/placeholder.jpg'),
        });
      }
    });
  }

  getImageUrl(event: EventModel): string {
    return this.imageUrls.get(event.uid) || 'assets/placeholder.jpg';
  }

  isInWishlist(eventUid: string): boolean {
    return this.wishlistUids.has(eventUid);
  }

  isWishlistLoading(eventUid: string): boolean {
    return this.wishlistLoading.has(eventUid);
  }

  toggleWishlist(event: EventModel, e: MouseEvent): void {
    e.stopPropagation();
    e.preventDefault();

    if (this.isInWishlist(event.uid)) {
      this.removeFromWishlist(event);
    } else {
      this.openTicketTypeModal(event);
    }
  }

  openTicketTypeModal(event: EventModel): void {
    this.ticketTypeModalEvent = event;
    this.ticketTypes = [];
    this.selectedTicketTypeUid = null;
    this.ticketTypesLoading = true;
    this.ticketTypeModalVisible = true;

    this.apiService.getAllEventTicketTypesByEventUuid(event.uid).subscribe({
      next: (res) => {
        this.ticketTypes = (res.body ?? []).filter(
          (t) => t.status !== 'SOLD_OUT'
        );
        if (this.ticketTypes.length === 1) {
          this.selectedTicketTypeUid = this.ticketTypes[0].uid;
        }
        this.ticketTypesLoading = false;
      },
      error: () => {
        this.notification.error('Error', 'Could not load ticket types.');
        this.ticketTypesLoading = false;
        this.ticketTypeModalVisible = false;
      },
    });
  }

  confirmAddToWishlist(): void {
    if (!this.ticketTypeModalEvent || !this.selectedTicketTypeUid) return;

    const event = this.ticketTypeModalEvent;
    const selectedType = this.ticketTypes.find(
      (t) => t.uid === this.selectedTicketTypeUid
    );
    if (!selectedType) return;
    const resolvedEventUid = selectedType.eventUid || event.uid;

    this.ticketTypeModalVisible = false;
    this.wishlistLoading.add(event.uid);

    this.apiService
      .addToWishlist({
        productUid: resolvedEventUid,
        eventUid: resolvedEventUid,
        ticketTypeUid: selectedType.uid,
        ticketTypeName: selectedType.name,
        quantity: 1,
      })
      .subscribe({
        next: (res: HttpResponse<WishlistItem>) => {
          if (res.body?.uid) {
            this.wishlistUids.add(event.uid);
            this.wishlistMap.set(event.uid, res.body.uid);
          }
          this.notification.success(
            'Added to Wishlist',
            `${event.name} (${selectedType.name}) saved.`
          );
          this.wishlistLoading.delete(event.uid);
        },
        error: () => {
          this.notification.error(
            'Error',
            'Could not add to wishlist. Try again.'
          );
          this.wishlistLoading.delete(event.uid);
        },
      });
  }

  cancelTicketTypeModal(): void {
    this.ticketTypeModalVisible = false;
    this.ticketTypeModalEvent = null;
  }

  formatTicketPrice(type: EventTicketType): string {
    if (type.free) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: type.currencyCode || 'USD',
    }).format(type.price);
  }

  private removeFromWishlist(event: EventModel): void {
    const wishlistItemUid = this.wishlistMap.get(event.uid);
    if (!wishlistItemUid) return;

    this.wishlistLoading.add(event.uid);
    this.apiService.removeFromWishlist(wishlistItemUid).subscribe({
      next: () => {
        this.wishlistUids.delete(event.uid);
        this.wishlistMap.delete(event.uid);
        this.notification.success(
          'Removed',
          `${event.name} removed from wishlist.`
        );
        this.wishlistLoading.delete(event.uid);
      },
      error: () => {
        this.notification.error(
          'Error',
          'Could not remove from wishlist. Try again.'
        );
        this.wishlistLoading.delete(event.uid);
      },
    });
  }

  formatPrice(min: string, max: string): string {
    if (!min && !max) return 'Free';
    if (min === max || !max) return min;
    if (!min) return max;
    return `${min} - ${max}`;
  }

  formatEventTime(time: string | { hour: number; minute: number }): string {
    if (typeof time === 'string') return time;
    if (time && typeof time === 'object') {
      const h = time.hour.toString().padStart(2, '0');
      const m = time.minute.toString().padStart(2, '0');
      return `${h}:${m}`;
    }
    return '';
  }
}

function dedupeAppend(
  curr: EventModel[],
  incoming: EventModel[]
): EventModel[] {
  if (incoming.length === 0) return curr;
  const seen = new Set(curr.map((e) => e.uid));
  const filtered = incoming.filter((e) => !seen.has(e.uid));
  return filtered.length === 0 ? curr : [...curr, ...filtered];
}
